// The visual field beside the brain (Mind › Vision): the stimulus the cortex is shown, in sync with the
// clock, and where each vertex of V1–V3 looks. ba-mind's run (sims/mind/benson14-vision-v3) is a Gaussian
// bar through fixation turning twice in 12 s, sampled at each vertex's predicted receptive-field centre
// (Benson 2014). The strip draws the bar from the run's own parameters (the stored frames match the
// formula to 2e-4, f16 rounding) and the centres from its visual_field_xy, coloured by what they see now
// or by area; the engine colours the cortex from the same frames. The cursor over the field reads out
// the position in degrees and which cortex sees it, until the embed reports the hovered vertex.

import { useEffect, useRef, useState } from 'react';
import { useAtlas, type SimSlot } from '../app/store.ts';
import { openedSlot } from '../app/sims.ts';
import { useLoaded } from '../ui/hooks.ts';
import { Segmented } from '../ui/primitives.tsx';
import { RAMPS, SERIES, sampleStops, type Stops } from '../ui/scales.ts';
import { Frame } from './Traces.tsx';
import { finishPaintStroke, liveImage, liveSquare, luminanceAt, paintStroke, useMindLive } from './mindLive.ts';
import type { FieldPoint } from './mindPaint.ts';

/** The run's stimulus: a 12° aperture, a bar 0.8° wide (σ), one turn of its normal in 12 s. */
export const FIELD = { radiusDeg: 12, sigmaDeg: 0.8, periodS: 12 } as const;

/** The bar's luminance at a point of the field (degrees; x right, y up) at time t: a Gaussian line through
 *  fixation whose normal turns at 30°/s, inside the aperture, as pipelines/mind/retinotopy/build.py sweeps it. */
export function barLuminance(x: number, y: number, t: number, f: typeof FIELD = FIELD): number {
  if (Math.hypot(x, y) > f.radiusDeg) return 0;
  const phi = (2 * Math.PI * t) / f.periodS;
  const d = x * Math.cos(phi) + y * Math.sin(phi);
  return Math.exp(-0.5 * (d / f.sigmaDeg) ** 2);
}

/** The bar's orientation from vertical, degrees anticlockwise, in [0, 180). */
export const barAngleDeg = (t: number, f: typeof FIELD = FIELD): number => ((((360 * t) / f.periodS) % 180) + 180) % 180;

/** Where a point of the field is, in words: "4.2° from fixation, upper right". */
export function fieldLabel(x: number, y: number): string {
  const r = Math.hypot(x, y);
  if (r < 0.25) return 'fixation';
  const out = `${r.toFixed(1)}° from fixation`;
  if (Math.abs(x) < 0.25) return `${out}, ${y > 0 ? 'upper' : 'lower'} vertical meridian`;
  if (Math.abs(y) < 0.25) return `${out}, ${x > 0 ? 'right' : 'left'} horizontal meridian`;
  return `${out}, ${y > 0 ? 'upper' : 'lower'} ${x > 0 ? 'right' : 'left'}`;
}

/** Which cortex sees a point: the left hemisphere takes the right half of the field. */
export const seenBy = (x: number): string => (Math.abs(x) < 0.25 ? 'both hemispheres' : x > 0 ? 'the left cortex' : 'the right cortex');

type Mode = 'seen' | 'area';
const AREA_NAME = ['V1', 'V2', 'V3'] as const;
/** Design's series colours, one per area: V1 blue, V2 teal, V3 orange. */
const AREA_COLOUR = [SERIES[0]!, SERIES[2]!, SERIES[1]!] as const;
const SIZE = 168;
const PAD = 8;

interface Field {
  xy: Float32Array;
  /** The run's image coordinates of each centre, top-left origin, over the 24° square (`live.uv`). */
  uv: Float32Array | null;
  valid: Uint8Array;
  area: Uint8Array;
  n: number;
  counts: number[];
}

/** The run's receptive-field centres and areas, from the sim the scene has open. */
async function loadField(path: string): Promise<Field | null> {
  const o = openedSlot('main');
  if (!o || o.format !== 'dynamics' || o.path !== path) return null;
  const [xy, valid, area, uv] = await Promise.all([o.sim.array('visual_field_xy'), o.sim.array('valid'), o.sim.array('visual_area'), o.sim.has('uv') ? o.sim.array('uv') : Promise.resolve(null)]);
  const counts = [0, 0, 0];
  for (let i = 0; i < valid.length; i++) if (valid[i] && area[i]! >= 1 && area[i]! <= 3) counts[area[i]! - 1]!++;
  return { xy: xy as Float32Array, uv: uv instanceof Float32Array ? uv : null, valid: valid as Uint8Array, area: area as Uint8Array, n: valid.length, counts };
}

/** The valid centre nearest a point of the field, within `withinDeg`. */
function nearest(f: Field, x: number, y: number, withinDeg = 0.8): { i: number; d: number } | null {
  let best: { i: number; d: number } | null = null;
  for (let i = 0; i < f.n; i++) {
    if (!f.valid[i]) continue;
    const d = Math.hypot(f.xy[2 * i]! - x, f.xy[2 * i + 1]! - y);
    if (d <= withinDeg && (!best || d < best.d)) best = { i, d };
  }
  return best;
}

function drawField(ctx: CanvasRenderingContext2D, field: Field | null, t: number, mode: Mode, hover: { x: number; y: number } | null, ramp: Stops) {
  const c = SIZE / 2;
  const s = (c - PAD) / FIELD.radiusDeg;
  const R = FIELD.radiusDeg * s;
  // The visitor's own image, when one is on: the 24° square whose inscribed circle is the aperture.
  const own = liveSquare();
  const ownImage = own ? liveImage() : null;
  ctx.clearRect(0, 0, SIZE, SIZE);
  ctx.save();
  ctx.beginPath();
  ctx.arc(c, c, R, 0, Math.PI * 2);
  ctx.clip();
  ctx.fillStyle = '#0b0e13';
  ctx.fillRect(0, 0, SIZE, SIZE);
  if (own) ctx.drawImage(own, c - R, c - R, 2 * R, 2 * R);
  else {
    // The bar: a Gaussian across its normal, which turns anticlockwise (canvas y runs down, so the normal's y flips).
    const phi = (2 * Math.PI * t) / FIELD.periodS;
    const nx = Math.cos(phi);
    const ny = -Math.sin(phi);
    const reach = 3 * FIELD.sigmaDeg * s;
    const g = ctx.createLinearGradient(c - nx * reach, c - ny * reach, c + nx * reach, c + ny * reach);
    for (let k = -3; k <= 3; k += 0.5) g.addColorStop((k + 3) / 6, `rgba(236, 240, 248, ${(0.92 * Math.exp(-0.5 * k * k)).toFixed(3)})`);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, SIZE, SIZE);
  }
  // Rings every 4° and the meridians.
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
  ctx.lineWidth = 1;
  for (const deg of [4, 8]) {
    ctx.beginPath();
    ctx.arc(c, c, deg * s, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.beginPath();
  ctx.moveTo(c - R, c);
  ctx.lineTo(c + R, c);
  ctx.moveTo(c, c - R);
  ctx.lineTo(c, c + R);
  ctx.stroke();
  // The centres: what each sees now on the cortex's own ramp (the slot's, design's drive for the sweep), or the area it belongs to.
  if (field) {
    const rgb: [number, number, number] = [0, 0, 0];
    ctx.lineWidth = 1;
    for (let i = 0; i < field.n; i++) {
      if (!field.valid[i]) continue;
      const x = field.xy[2 * i]!;
      const y = field.xy[2 * i + 1]!;
      if (mode === 'area') ctx.fillStyle = AREA_COLOUR[field.area[i]! - 1] ?? '#fff';
      else {
        const seen = ownImage && field.uv ? luminanceAt(ownImage, field.uv[2 * i]!, field.uv[2 * i + 1]!) : barLuminance(x, y, t);
        sampleStops(ramp, seen, rgb);
        ctx.fillStyle = `rgb(${rgb[0] | 0}, ${rgb[1] | 0}, ${rgb[2] | 0})`;
      }
      ctx.beginPath();
      ctx.arc(c + x * s, c - y * s, 2.2, 0, Math.PI * 2);
      ctx.fill();
      if (mode === 'seen') {
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.22)';
        ctx.stroke();
      }
    }
  }
  ctx.restore();
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.18)';
  ctx.beginPath();
  ctx.arc(c, c, R, 0, Math.PI * 2);
  ctx.stroke();
  if (hover && Math.hypot(hover.x, hover.y) <= FIELD.radiusDeg) {
    ctx.strokeStyle = '#fff';
    ctx.beginPath();
    ctx.arc(c + hover.x * s, c - hover.y * s, 4.5, 0, Math.PI * 2);
    ctx.stroke();
  }
}

const signed = (v: number) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(1)}°`;

export function VisualFieldStrip({ main }: { main: SimSlot }) {
  const path = main.path;
  const field = useLoaded(() => loadField(path), [path]);
  const t = useAtlas((s) => s.time);
  const [mode, setMode] = useState<Mode>('seen');
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const stroke = useRef<{ id: number; last: FieldPoint } | null>(null);
  const painting = useMindLive((s) => s.kind === 'paint' && s.status === 'on');
  const f = field.value;
  const ramp = RAMPS[main.ramp ?? 'activity'];
  useEffect(() => () => { stroke.current = null; finishPaintStroke(); }, [painting]);

  useEffect(() => {
    const el = canvas.current;
    const ctx = el?.getContext('2d');
    if (!el || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    if (el.width !== SIZE * dpr) el.width = el.height = SIZE * dpr;
    let raf = 0;
    const paint = () => {
      raf = 0;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      drawField(ctx, f, useAtlas.getState().time, mode, hover, ramp);
    };
    const ask = () => {
      if (!raf) raf = requestAnimationFrame(paint);
    };
    ask();
    const un = useAtlas.subscribe((s) => s.time, ask);
    const unLive = useMindLive.subscribe(ask);
    return () => {
      un();
      unLive();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [f, mode, hover, ramp]);

  const toField = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const s = (SIZE / 2 - PAD) / FIELD.radiusDeg;
    return { x: ((e.clientX - r.left) * SIZE / r.width - SIZE / 2) / s, y: -((e.clientY - r.top) * SIZE / r.height - SIZE / 2) / s };
  };
  const endStroke = (e: React.PointerEvent<HTMLCanvasElement>, finalPoint: boolean) => {
    if (stroke.current?.id !== e.pointerId) return;
    if (finalPoint) paintStroke(stroke.current.last, toField(e));
    stroke.current = null;
    finishPaintStroke();
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };
  const inField = hover && Math.hypot(hover.x, hover.y) <= FIELD.radiusDeg ? hover : null;
  const near = inField && f ? nearest(f, inField.x, inField.y) : null;
  const own = useMindLive((s) => (s.frames > 0 ? s : null));
  const ownImage = own ? liveImage() : null;
  // What the cursor's point sees: the image's luminance there (its uv is the field's x right, y down, over 24°), else the bar's.
  const seenAt = (x: number, y: number) => (ownImage ? luminanceAt(ownImage, 0.5 + x / 24, 0.5 - y / 24) : barLuminance(x, y, t));
  const total = f ? f.counts.reduce((a, b) => a + b, 0) : 0;
  return (
    <Frame
      title="Visual field"
      sub={own ? `${own.kind === 'camera' ? 'your camera' : own.kind === 'paint' ? 'your drawing' : own.kind === 'wedge' || own.kind === 'ring' ? `a ${own.kind}` : 'your image'} as the stimulus, and where V1–V3 look` : 'the bar the cortex is shown, and where V1–V3 look'}
      controls={
        <Segmented
          size="sm"
          label="Colour the centres by"
          value={mode}
          options={[
            { value: 'seen', label: 'Seen now', hint: 'Each centre on the activity scale: the stimulus’s luminance where it looks' },
            { value: 'area', label: 'Area', hint: 'V1, V2 or V3' },
          ]}
          onChange={setMode}
        />
      }
    >
      <div className="visual-field">
        <canvas ref={canvas} style={{ width: SIZE, height: SIZE }} data-paint={painting}
          aria-label={painting ? 'Paint the visual field with a 1° white brush' : 'The visual field: a 12° aperture with the stimulus and the receptive-field centres'}
          onPointerDown={(e) => {
            if (!painting || e.button !== 0 || stroke.current) return;
            e.preventDefault();
            const point = toField(e);
            stroke.current = { id: e.pointerId, last: point };
            e.currentTarget.setPointerCapture(e.pointerId);
            paintStroke(point);
          }}
          onPointerMove={(e) => {
            const point = toField(e);
            setHover(point);
            if (stroke.current?.id === e.pointerId) {
              paintStroke(stroke.current.last, point);
              stroke.current.last = point;
            }
          }}
          onPointerUp={(e) => endStroke(e, true)}
          onPointerCancel={(e) => endStroke(e, false)}
          onLostPointerCapture={(e) => endStroke(e, false)}
          onPointerLeave={() => setHover(null)} />
        <div className="vf-legend">
          <p className="vf-readout ba-type-small num">
            {inField
              ? `x ${signed(inField.x)} · y ${signed(inField.y)} — ${fieldLabel(inField.x, inField.y)}, seen by ${seenBy(inField.x)} · ${own ? 'luminance' : 'bar'} ${seenAt(inField.x, inField.y).toFixed(2)}`
              : own
                ? own.kind === 'camera'
                  ? `Your camera, live · ${own.frames.toLocaleString()} frames · the 24° field, its 12° aperture shown`
                  : own.kind === 'paint'
                    ? 'Your drawing · drag a white stroke inside the 12° aperture'
                  : own.kind === 'wedge'
                    ? `Wedge ${own.waveValue.toFixed(0)}° · 30° wide · ${own.wavePlaying ? 'one turn in 12 s' : 'paused'}`
                  : own.kind === 'ring'
                    ? `Ring ${own.waveValue.toFixed(2)}° from fixation · 1° wide · ${own.wavePlaying ? 'outward in 8 s' : 'paused'}`
                  : `Your image, ${own.label ?? ''} · the 24° field, its 12° aperture shown`
                : `Bar ${barAngleDeg(t).toFixed(0)}° from vertical, turning at 30°/s · ${t.toFixed(1)} s of 12`}
          </p>
          <p className="vf-near ba-type-caption muted">
            {near && f ? `Nearest centre: ${AREA_NAME[f.area[near.i]! - 1] ?? 'V?'}, ${near.d.toFixed(1)}° away, a vertex of the ${near.i < f.n / 2 ? 'left' : 'right'} cortex` : f ? `${total} receptive-field centres inside 12°` : field.error ? field.error : 'Reading the centres'}
          </p>
          <ul className="vf-key ba-type-caption">
            {AREA_NAME.map((name, i) => (
              <li key={name}>
                <span className="swatch" style={{ background: AREA_COLOUR[i] }} />
                <span>{name}</span>
                <span className="muted num">{f ? `${f.counts[i]} centres` : ''}</span>
              </li>
            ))}
          </ul>
          <p className="vf-note ba-type-caption muted">The left cortex sees the right half of the field; the cuneus, above the calcarine sulcus, sees the lower half.</p>
        </div>
      </div>
    </Frame>
  );
}
